import aprobacionPlanoApi from "@/api/AprobacionPlano";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import TableThree from "@/components/Tables/TableThree";
import { toKeyValue } from "@/utils/convert";
import { IoMdPrint } from "react-icons/io";

export const Step2 = ({
  user,
  show,
  currentStep,
  setCurrentStep,
  setPrevCarg,
  setInfoCert,
  data = [],
  setData,
}) => {
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingRow, setLoadingRow] = useState(null);
  const [cedularuc, setCedularuc] = useState("");

  useEffect(() => {
    if (user?.cedularuc) setCedularuc(user.cedularuc);
  }, [user?.cedularuc]);

  const listarCertificados = useCallback(async () => {
    if (!cedularuc) return;

    setIsLoading(true);
    try {
      const resp = await aprobacionPlanoApi.getAprobaciones(cedularuc);

      if ((resp.data?.data ?? []).length <= 0) {
        show({
          type: "warning",
          title: "Sin Resultados",
          message: "No posee certificados de obra disponibles para generar",
        });
        setData([]);
        setPrevCarg(true);
        return;
      }

      const resul = toKeyValue(resp.data.data);
      const resultado = resul.map((datos) => ({
        codigo: datos.codigo,
        CLAVEPRE: datos.CLAVEPRE,
        propietario: datos.propietario,
        DIRECCION: datos.DIRECCION,
        fechainicio: datos.fechainicio,
        tipo: datos.tipo,
      }));

      setData(resultado);
      setPrevCarg(true);
    } catch (error) {
      if (error.response?.data) {
        show({
          type: "warning",
          title: error.response.data.error,
          message: error.response.data.message,
        });
      } else {
        const { name, message } = error;
        show({ type: "error", title: name, message });
      }
    } finally {
      setIsLoading(false);
    }
  }, [cedularuc, show, setData, setPrevCarg]);

  useEffect(() => {
    if (!cedularuc) return;
    if (data.length !== 0) return;

    listarCertificados();
  }, [cedularuc, data.length, listarCertificados]);

  const handlePrint = useCallback(
    (codigo, tipo) => {
      setLoadingRow(codigo);
      setInfoCert({ codigo, tipo });
      setCurrentStep(currentStep + 1);
    },
    [currentStep, setCurrentStep, setInfoCert]
  );

  const datatable = useMemo(
    () => ({
      head: [
        { title: "N° Tramite", key: "codigo", type: "text", sort: true },
        { title: "Clave Predial", key: "CLAVEPRE", type: "text", sort: true },
        { title: "Propietario", key: "propietario", type: "text", sort: true },
        { title: "Dirección", key: "DIRECCION", type: "text", sort: true },
        { title: "Fecha de Inicio", key: "fechainicio", type: "text", sort: true },
        {
          title: "Acción",
          type: "html",
          extend: (row) => (
            <button
              onClick={() => handlePrint(row.codigo, row.tipo)}
              className="btn btn-secundary rounded-lg px-4 py-2"
              disabled={loadingRow === row.codigo}
              type="button"
            >
              {loadingRow === row.codigo ? (
                <span className="loading loading-spinner"></span>
              ) : (
                <IoMdPrint size={22} />
              )}
            </button>
          ),
        },
      ],
      body: data,
    }),
    [data, handlePrint, loadingRow]
  );

  return (
    <TableThree
      data={datatable}
      page={page}
      dataCount={datatable.body.length}
      numbered={true}
      isLoading={isLoading}
      onChange={setPage}
    />
  );
};

export default Step2;

Step2.propTypes = {
  data: PropTypes.array,
  user: PropTypes.object,
  show: PropTypes.func.isRequired,
  currentStep: PropTypes.number.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  setPrevCarg: PropTypes.func.isRequired,
  setInfoCert: PropTypes.func.isRequired,
  setData: PropTypes.func.isRequired,
};
